import React from 'react';
import { Badge } from '@/components/ui/badge';
import { CheckCircle } from 'lucide-react';

interface SyncDataTypesDisplayProps {
  showTitle?: boolean;
  size?: 'sm' | 'md';
  className?: string;
}

// Data types that are always synced (matches backend scheduled sync architecture)
const SYNC_DATA_TYPES = [
  { 
    name: 'Contacts', 
    description: 'Customer and vendor contacts'
  },
  { 
    name: 'Accounts', 
    description: 'Chart of accounts structure'
  },
  { 
    name: 'Invoices', 
    description: 'Customer invoices and sales'
  },
  { 
    name: 'Bank Transactions', 
    description: 'Bank account transactions'
  },
  { 
    name: 'Manual Journals', 
    description: 'Manual journal entries'
  },
  { 
    name: 'Payments', 
    description: 'Payment transactions'
  },
  { 
    name: 'Spend Money', 
    description: 'Spend Money (Xero) / Expenses (QuickBooks Online)'
  },
  { 
    name: 'Bills', 
    description: 'Vendor bills and purchases (triggers AI processing)',
    isSpecial: true // Bills are processed last with Stage 2 heavy processing
  }
];

export function SyncDataTypesDisplay({ 
  showTitle = false, 
  size = 'md', 
  className = '' 
}: SyncDataTypesDisplayProps) {
  const isSmall = size === 'sm';

  return (
    <div className={`space-y-3 ${className}`}>
      {showTitle && (
        <div className="space-y-1">
          <h4 className={`font-medium ${isSmall ? 'text-sm' : 'text-base'}`}>
            Synchronized Data Types
          </h4>
          <p className={`text-muted-foreground ${isSmall ? 'text-xs' : 'text-sm'}`}>
            The following data is automatically synchronized every 24 hours for system integrity:
          </p>
        </div>
      )}
      
      <div className={`grid gap-2 ${isSmall ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
        {SYNC_DATA_TYPES.map((dataType) => (
          <div 
            key={dataType.name}
            className={`flex items-start space-x-2 p-2 rounded-md border bg-card ${
              dataType.isSpecial ? 'border-blue-200 bg-blue-50' : ''
            }`}
          >
            <CheckCircle 
              className={`${isSmall ? 'h-3 w-3' : 'h-4 w-4'} text-green-500 mt-0.5 flex-shrink-0`} 
            />
            <div className="flex-1 min-w-0">
              <div className="flex items-center space-x-2">
                <span className={`font-medium ${isSmall ? 'text-xs' : 'text-sm'}`}>
                  {dataType.name}
                </span>
                {dataType.isSpecial && (
                  <Badge variant="secondary" className="text-xs">
                    AI Processing
                  </Badge>
                )}
              </div>
              <p className={`text-muted-foreground ${isSmall ? 'text-xs' : 'text-xs'} mt-0.5`}>
                {dataType.description}
              </p>
            </div>
          </div>
        ))}
      </div>
      
      <div className={`${isSmall ? 'text-xs' : 'text-sm'} text-muted-foreground bg-muted p-3 rounded-md`}>
        <div className="flex items-start space-x-2">
          <span className="text-blue-600 font-medium">ℹ️</span>
          <div>
            <p className="font-medium mb-1">Automatic Data Selection</p>
            <p>
              All data types are synchronized to ensure complete financial data integrity. 
              Bills undergo additional AI processing for prepayment detection and document analysis.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}